/**
 * SAM Rumah AI Concierge — Chat Bubble Widget
 * V4.16.20 — Prop-tech redesign.
 *
 *  - Brand-gradient header (135deg #4E50FF → #6366F1)
 *  - Desktop: 420×640 floating panel | Mobile: full-screen bottom sheet
 *  - Glass-morphism floating bubble with subtle "online" pulse
 *  - Quick-action chips for top customer + agent flows
 *  - Inline property cards rendered from `mentioned_properties`
 *  - Distinct user (gradient pill) vs AI (white card + SAM AI badge) styles
 *  - "SAM is checking…" typing state with animated building icon
 *  - "Powered by SAM AI" footer
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { chatAPI } from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  MessageCircle, X, Send, Loader2, Plus, Trash2, ChevronLeft,
  Building2, Sparkles, MapPin, Home,
  Calculator, Calendar, FileText, Tag,
  Wallet, BarChart3, Share2, User as UserIcon,
  Search,
} from "lucide-react";

// Icon lookup for admin-configured quick actions (icon name → component)
const ICON_MAP = {
  Home, Calculator, Calendar, FileText, Tag, Wallet, BarChart3, Share2,
  Sparkles, MessageCircle, MapPin, Building2, Search,
};

// Hardcoded fallbacks — used when /chat/config fails (offline / fresh tenant)
const CUSTOMER_QUICK_ACTIONS = [
  { id: "find_under_500", icon: Home,      label: "Find under RM 500k",
    prompt: "Show me properties under RM 500,000 with at least 2 bedrooms." },
  { id: "mortgage_calc",  icon: Calculator, label: "Mortgage calculator",
    prompt: "I earn RM 8,000/month. What property price can I afford with a 30-year loan at 4.5%?" },
  { id: "stamp_duty",     icon: Tag,        label: "Stamp duty calc",
    prompt: "Calculate stamp duty + legal fees for a RM 750,000 condo." },
  { id: "booking_status", icon: Calendar,   label: "Track my booking",
    prompt: "What's the status of my most recent booking?" },
  { id: "documents",      icon: FileText,   label: "SPA documents",
    prompt: "What documents do I need to prepare for SPA signing?" },
];

const AGENT_QUICK_ACTIONS = [
  { id: "my_commission",  icon: Wallet,     label: "My commission",
    prompt: "Summarise my pending commission and recent claims." },
  { id: "my_pipeline",    icon: BarChart3,  label: "Booking pipeline",
    prompt: "Show my booking pipeline grouped by status." },
  { id: "referral_share", icon: Share2,     label: "Share my referral",
    prompt: "Generate a WhatsApp-friendly message I can use to share my referral code with potential clients." },
  { id: "mortgage_calc",  icon: Calculator, label: "Mortgage calculator",
    prompt: "Help me calculate mortgage affordability for a client earning RM 10,000/month." },
];

const formatRM = (v) => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  if (n >= 1_000_000) return `RM ${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `RM ${(n / 1_000).toFixed(0)}k`;
  return `RM ${n.toFixed(0)}`;
};

// ─── Inline property card rendered from `mentioned_properties` ─────────
const InlinePropertyCard = ({ prop, onView }) => {
  const priceLabel = prop.starting_price && prop.max_price && prop.max_price !== prop.starting_price
    ? `${formatRM(prop.starting_price)} – ${formatRM(prop.max_price)}`
    : prop.starting_price ? `From ${formatRM(prop.starting_price)}` : "Contact for pricing";

  return (
    <div
      onClick={() => onView(prop.id)}
      className="group mt-2 rounded-2xl border border-[#E5E7FF] bg-white overflow-hidden hover:shadow-lg hover:border-[#4E50FF]/40 transition-all cursor-pointer"
      data-testid={`chat-property-card-${prop.id}`}
    >
      <div className="relative h-28 bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF] overflow-hidden">
        {prop.primary_image ? (
          <img
            src={prop.primary_image}
            alt={prop.name}
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <Building2 className="h-10 w-10 text-[#4E50FF]/40" />
          </div>
        )}
        <div className="absolute top-2 right-2 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold text-[#4E50FF] shadow-sm">
          {priceLabel}
        </div>
      </div>
      <div className="p-3">
        <div className="text-sm font-semibold text-gray-900 line-clamp-1">{prop.name}</div>
        {prop.location && (
          <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
            <MapPin className="h-3 w-3" /> {prop.location}
          </div>
        )}
        <div className="mt-2 flex items-center justify-between">
          <span className="inline-flex items-center gap-1 rounded-md bg-[#EEF2FF] px-2 py-0.5 text-[10px] font-medium text-[#4E50FF] uppercase tracking-wide">
            {prop.property_type || "Property"}
          </span>
          <span className="text-[11px] font-medium text-[#4E50FF] group-hover:translate-x-0.5 transition">
            View →
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── SAM AI typing indicator with personality ──────────────────────────
const TypingIndicator = () => (
  <div className="flex items-start gap-2">
    <div className="h-8 w-8 rounded-2xl bg-gradient-to-br from-[#4E50FF] to-[#6366F1] flex items-center justify-center shrink-0 shadow-sm">
      <Sparkles className="h-4 w-4 text-white" />
    </div>
    <div className="rounded-2xl rounded-tl-md bg-white border border-gray-100 shadow-sm px-3.5 py-2.5">
      <div className="flex items-center gap-2">
        <Building2 className="h-3.5 w-3.5 text-[#4E50FF] animate-pulse" />
        <span className="text-xs text-gray-600 italic">SAM is checking listings…</span>
      </div>
    </div>
  </div>
);

const ChatBubble = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [view, setView] = useState("sessions"); // 'sessions' | 'chat'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Determine user role for the right quick-action set
  const userRole = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return "customer";
      return JSON.parse(raw).role || "customer";
    } catch { return "customer"; }
  }, [open]);

  // V4.16.21 — Admin-configurable quick actions. Fetch on first open;
  // fall back to the hardcoded defaults if the public config endpoint
  // is unreachable.
  const [remoteCustomerQA, setRemoteCustomerQA] = useState(null);
  const [remoteAgentQA, setRemoteAgentQA] = useState(null);
  useEffect(() => {
    if (!open) return;
    if (remoteCustomerQA && remoteAgentQA) return;
    (async () => {
      try {
        const res = await chatAPI.getPublicConfig();
        setRemoteCustomerQA(res.data?.customer_quick_actions || null);
        setRemoteAgentQA(res.data?.agent_quick_actions || null);
      } catch {
        // ignore — use defaults
      }
    })();
  }, [open, remoteCustomerQA, remoteAgentQA]);

  // Hydrate icon strings → React components, fall back to Sparkles.
  const hydrate = (list) => (list || []).map((qa) => ({
    ...qa,
    icon: ICON_MAP[qa.icon] || Sparkles,
  }));
  const quickActions = userRole === "agent"
    ? (remoteAgentQA ? hydrate(remoteAgentQA) : AGENT_QUICK_ACTIONS)
    : (remoteCustomerQA ? hydrate(remoteCustomerQA) : CUSTOMER_QUICK_ACTIONS);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => { scrollToBottom(); }, [messages, sending]);

  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const res = await chatAPI.listSessions();
      setSessions(res.data?.sessions || []);
    } catch { /* swallow — non-critical session load */ } finally { setLoadingSessions(false); }
  }, []);

  useEffect(() => { if (open) loadSessions(); }, [open, loadSessions]);

  const handleNewChat = async () => {
    try {
      const res = await chatAPI.createSession();
      const session = res.data;
      setSessions(prev => [session, ...prev]);
      setActiveSession(session);
      setMessages([]);
      setView("chat");
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch { toast.error("Failed to create chat"); }
  };

  const handleSelectSession = async (session) => {
    setActiveSession(session);
    setView("chat");
    try {
      const res = await chatAPI.getHistory(session.id);
      setMessages(res.data?.messages || []);
    } catch { toast.error("Failed to load chat"); }
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await chatAPI.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (activeSession?.id === sessionId) {
        setActiveSession(null);
        setMessages([]);
        setView("sessions");
      }
    } catch { toast.error("Failed to delete"); }
  };

  const handleSend = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    // Auto-create a session if none active (quick-action shortcut).
    let session = activeSession;
    if (!session) {
      try {
        const res = await chatAPI.createSession();
        session = res.data;
        setSessions(prev => [session, ...prev]);
        setActiveSession(session);
        setView("chat");
      } catch { toast.error("Failed to create chat"); return; }
    }
    if (!overrideText) setInput("");
    setSending(true);

    const tempUserMsg = {
      id: "temp-user-" + Date.now(),
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const res = await chatAPI.sendMessage(session.id, text);
      const { user_message, ai_response } = res.data;
      setMessages(prev => [
        ...prev.filter(m => m.id !== tempUserMsg.id),
        user_message,
        ai_response,
      ]);
      if (session.message_count === 0) {
        setSessions(prev => prev.map(s =>
          s.id === session.id ? { ...s, title: text.slice(0, 50), message_count: 2 } : s
        ));
      }
    } catch {
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      toast.error("Failed to send message");
    } finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleViewProperty = (propId) => {
    if (!propId) return;
    navigate(`/property/${propId}`);
    setOpen(false);
  };

  // ─── Floating bubble (collapsed) ─────────────────────────────────────
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 group"
        data-testid="chat-bubble-btn"
        aria-label="Open SAM Rumah AI Concierge"
      >
        <span className="absolute inset-0 rounded-full bg-[#4E50FF]/30 animate-ping" />
        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#4E50FF] to-[#6366F1] text-white shadow-[0_8px_25px_rgba(78,80,255,0.45)] group-hover:scale-105 group-hover:shadow-[0_10px_30px_rgba(78,80,255,0.55)] transition-all">
          <Sparkles className="h-6 w-6" />
          <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 border-2 border-white" />
        </span>
      </button>
    );
  }

  // ─── Open widget ─────────────────────────────────────────────────────
  return (
    <div
      className={[
        "fixed z-50 flex flex-col overflow-hidden shadow-2xl",
        // Mobile: full-screen bottom sheet
        "inset-x-0 bottom-0 top-0 h-full w-full rounded-none",
        // Desktop (md+): floating panel bottom-right
        "md:inset-auto md:bottom-6 md:right-6 md:h-[640px] md:w-[420px] md:top-auto md:rounded-3xl md:border md:border-[#E5E7FF]",
        "bg-gradient-to-b from-[#FAFBFF] to-white",
      ].join(" ")}
      data-testid="chat-widget"
    >
      {/* Header — brand gradient */}
      <div
        className="px-4 py-3.5 flex items-center justify-between shrink-0 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #4E50FF 0%, #6366F1 100%)" }}
      >
        {/* Decorative grain */}
        <div className="absolute inset-0 opacity-10 mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 0.5px, transparent 0.5px)", backgroundSize: "8px 8px" }} />
        <div className="flex items-center gap-2.5 relative">
          {view === "chat" && (
            <button onClick={() => setView("sessions")}
              className="hover:bg-white/20 rounded-lg p-1 transition" data-testid="chat-back-btn">
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          <div className="h-9 w-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="font-semibold text-sm tracking-tight">SAM Rumah AI</div>
            <div className="text-[10.5px] text-white/80 flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
              Concierge online
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 relative">
          {view === "sessions" && (
            <button onClick={handleNewChat}
              className="hover:bg-white/20 rounded-lg p-1.5 transition" data-testid="new-chat-btn"
              aria-label="New chat">
              <Plus className="h-4 w-4" />
            </button>
          )}
          <button onClick={() => setOpen(false)}
            className="hover:bg-white/20 rounded-lg p-1.5 transition" data-testid="close-chat-btn"
            aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ─── Sessions list view ─────────────────────────────────────── */}
      {view === "sessions" && (
        <div className="flex-1 overflow-y-auto" data-testid="chat-sessions-view">
          {loadingSessions ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-[#4E50FF]" />
            </div>
          ) : sessions.length === 0 ? (
            // ─── First-time / empty state ───
            <div className="px-5 py-6">
              <div className="flex flex-col items-center text-center mb-5">
                <div className="h-16 w-16 rounded-3xl bg-gradient-to-br from-[#4E50FF] to-[#6366F1] flex items-center justify-center shadow-lg mb-3">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 text-base">Your AI Property Concierge</h3>
                <p className="text-xs text-gray-500 mt-1.5 leading-relaxed px-2">
                  Find homes, calculate mortgages, track bookings, and more — powered by SAM AI.
                </p>
              </div>

              {/* Quick-action chips */}
              <div className="grid grid-cols-2 gap-2 mb-4" data-testid="chat-quick-actions">
                {quickActions.map((qa) => {
                  const Icon = qa.icon;
                  return (
                    <button
                      key={qa.id}
                      onClick={() => handleSend(qa.prompt)}
                      className="text-left p-3 rounded-2xl border border-[#E5E7FF] bg-white hover:bg-[#EEF2FF] hover:border-[#4E50FF]/40 transition group"
                      data-testid={`chat-qa-${qa.id}`}
                    >
                      <div className="h-7 w-7 rounded-lg bg-[#EEF2FF] group-hover:bg-white flex items-center justify-center mb-2 transition">
                        <Icon className="h-3.5 w-3.5 text-[#4E50FF]" />
                      </div>
                      <div className="text-xs font-medium text-gray-900 leading-tight">{qa.label}</div>
                    </button>
                  );
                })}
              </div>

              <Button
                onClick={handleNewChat}
                className="w-full bg-gradient-to-r from-[#4E50FF] to-[#6366F1] hover:opacity-90 text-sm gap-2 rounded-xl shadow-md"
                data-testid="start-chat-btn"
              >
                <MessageCircle className="h-4 w-4" /> Start a new chat
              </Button>
            </div>
          ) : (
            <div className="p-3 space-y-1.5">
              <button
                onClick={handleNewChat}
                className="w-full flex items-center justify-center gap-2 p-2.5 rounded-xl bg-gradient-to-r from-[#4E50FF] to-[#6366F1] text-white text-sm font-medium shadow-sm hover:opacity-90 transition"
                data-testid="new-chat-row-btn"
              >
                <Plus className="h-4 w-4" /> New conversation
              </button>
              {sessions.map(s => (
                <div
                  key={s.id}
                  onClick={() => handleSelectSession(s)}
                  className="flex items-center justify-between p-3 rounded-xl hover:bg-[#EEF2FF]/60 cursor-pointer transition group border border-transparent hover:border-[#E5E7FF]"
                  data-testid={`session-${s.id}`}
                >
                  <div className="min-w-0 flex-1 flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-[#EEF2FF] flex items-center justify-center shrink-0">
                      <MessageCircle className="h-4 w-4 text-[#4E50FF]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{s.title || "New Chat"}</p>
                      <p className="text-[11px] text-gray-400">{s.message_count || 0} messages</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteSession(e, s.id)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 rounded-lg transition"
                    aria-label="Delete chat"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Active chat view ───────────────────────────────────────── */}
      {view === "chat" && (
        <>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="h-14 w-14 mx-auto rounded-2xl bg-gradient-to-br from-[#4E50FF] to-[#6366F1] flex items-center justify-center shadow-md mb-3">
                  <Sparkles className="h-7 w-7 text-white" />
                </div>
                <p className="text-sm font-semibold text-gray-900">Hi! I&apos;m SAM, your AI concierge.</p>
                <p className="text-xs text-gray-500 mt-1 px-4 leading-relaxed">
                  Try one of these to get started:
                </p>
                <div className="grid grid-cols-2 gap-1.5 mt-3 px-2" data-testid="chat-inline-qa">
                  {quickActions.slice(0, 4).map((qa) => {
                    const Icon = qa.icon;
                    return (
                      <button
                        key={qa.id}
                        onClick={() => handleSend(qa.prompt)}
                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-full border border-[#E5E7FF] bg-white text-xs text-gray-700 hover:border-[#4E50FF]/40 hover:bg-[#EEF2FF] transition"
                        data-testid={`chat-inline-qa-${qa.id}`}
                      >
                        <Icon className="h-3 w-3 text-[#4E50FF]" /> {qa.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div key={msg.id} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "assistant" && (
                  <div className="h-8 w-8 rounded-2xl bg-gradient-to-br from-[#4E50FF] to-[#6366F1] flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                )}
                <div className={`max-w-[82%] ${msg.role === "user" ? "items-end" : "items-start"} flex flex-col`}>
                  {msg.role === "assistant" && (
                    <span className="text-[10px] font-medium uppercase tracking-wider text-[#4E50FF]/70 mb-1 ml-1">
                      SAM AI
                    </span>
                  )}
                  <div
                    className={[
                      "rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                      msg.role === "user"
                        ? "bg-gradient-to-br from-[#4E50FF] to-[#6366F1] text-white rounded-br-md shadow-[0_4px_14px_rgba(78,80,255,0.25)]"
                        : "bg-white border border-gray-100 text-gray-800 rounded-tl-md shadow-sm",
                    ].join(" ")}
                  >
                    {msg.role === "assistant" ? (
                      <div
                        className="whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{
                          __html: (msg.content || "")
                            .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                            // strip naked (ID: XXX) tags — they belong to the card UI
                            .replace(/\s*\(ID:\s*[A-Z0-9\-_]+\)/gi, "")
                            .replace(/\n/g, "<br/>"),
                        }}
                      />
                    ) : (
                      <span>{msg.content}</span>
                    )}

                    {/* Booking-created confirmation badge */}
                    {msg.booking_created && (
                      <div className="mt-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800">
                        <div className="flex items-center gap-1.5 font-semibold mb-1">
                          <Calendar className="h-3.5 w-3.5" /> Viewing booked!
                        </div>
                        <div>
                          {msg.booking_created.property_name}<br />
                          {msg.booking_created.preferred_date} at {msg.booking_created.preferred_time}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Inline property cards (V4.16.20) */}
                  {msg.role === "assistant" && Array.isArray(msg.mentioned_properties) && msg.mentioned_properties.length > 0 && (
                    <div className="w-full mt-1">
                      {msg.mentioned_properties.slice(0, 3).map((p) => (
                        <InlinePropertyCard key={p.id} prop={p} onView={handleViewProperty} />
                      ))}
                    </div>
                  )}
                </div>
                {msg.role === "user" && (
                  <div className="h-8 w-8 rounded-2xl bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                    <UserIcon className="h-4 w-4 text-gray-600" />
                  </div>
                )}
              </div>
            ))}

            {sending && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          {/* Input bar */}
          <div className="border-t border-gray-100 px-3 py-2.5 shrink-0 bg-white">
            <div className="flex items-center gap-2 bg-[#F5F6FF] rounded-2xl px-3 py-1.5 border border-transparent focus-within:border-[#4E50FF]/40 transition">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask SAM anything…"
                className="flex-1 border-0 bg-transparent text-sm focus-visible:ring-0 focus-visible:ring-offset-0 px-0 h-9"
                disabled={sending}
                data-testid="chat-input"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || sending}
                className="h-8 w-8 rounded-xl bg-gradient-to-br from-[#4E50FF] to-[#6366F1] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-white shadow-sm transition shrink-0"
                data-testid="chat-send-btn"
                aria-label="Send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <div className="text-[10px] text-gray-400 mt-1.5 text-center">
              Powered by SAM AI
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatBubble;
